import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  VpnKey,
  AdminPanelSettings,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setMessage('Invalid or missing reset token');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      // Validate passwords
      if (passwords.newPassword.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      if (passwords.newPassword !== passwords.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      // Send reset request
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';
      const response = await fetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: passwords.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message);
        setIsSuccess(true);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setMessage(data.error || 'Failed to reset password');
        setIsSuccess(false);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to reset password');
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof typeof passwords) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPasswords(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (message) setMessage('');
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              textAlign: 'center',
            }}
          >
            <AdminPanelSettings sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Invalid Reset Link
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              The password reset link is invalid or has expired.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                background: 'linear-gradient(45deg, #000000 30%, #333333 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #333333 30%, #555555 90%)',
                },
              }}
            >
              Back to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            {isSuccess ? (
              <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            ) : (
              <AdminPanelSettings sx={{ fontSize: 48, color: '#000', mb: 1 }} />
            )}
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {isSuccess ? 'Password Reset Successfully!' : '🐾 Reset Password'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isSuccess 
                ? 'You can now login with your new password' 
                : 'Enter your new password below'
              }
            </Typography>
          </Box>

          {/* Message Alert */}
          {message && (
            <Alert severity={isSuccess ? 'success' : 'error'} sx={{ mb: 3 }}>
              {message}
              {isSuccess && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Redirecting to login in 3 seconds...
                </Typography>
              )}
            </Alert>
          )}

          {!isSuccess && (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.newPassword}
                onChange={handleInputChange('newPassword')}
                margin="normal"
                required
                helperText="Password must be at least 8 characters long"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting || !passwords.newPassword || !passwords.confirmPassword}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #000000 30%, #333333 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #333333 30%, #555555 90%)',
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}

          {/* Back to Login Button */}
          <Box textAlign="center" mt={2}>
            <Button
              variant="text"
              onClick={() => navigate('/')}
              disabled={isSubmitting}
            >
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;